import { TrashSimpleIcon, WarningCircleIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import invariant from 'tiny-invariant'
import { DefaultButton } from '../../../components/buttons/default-button.tsx'
import { GhostButton } from '../../../components/buttons/ghost-button.tsx'
import { useShowMutationError } from '../../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../../services/toast-service.tsx'
import { useDeleteIntegration } from '../../../utils/api-client/api-hooks.ts'
import type { Integration, IntegrationInUseProblem } from '../../../utils/api-client/api-schemas.ts'
import { pluralize } from '../../../utils/text-utils.ts'
import * as styles from './delete-integration-modal.css.ts'
import { IntegrationBrandIcon } from './integration-icon.tsx'
import {
  getHasIntegrationSetupForm,
  getInstanceName,
  getIntegrationInUseProblem,
  getProviderLabel,
  reportRoleRefusal,
} from './integration-instance-utils.ts'
import { getWebhookDeleteWarning } from './integration-webhook.ts'

export type DeleteIntegrationModalProps = {
  integration: Integration
  onCancel: () => unknown
  onSuccess: () => unknown
}

// The standard delete-confirmation modal (mirrors the coding-guidelines / repository patterns): owns
// the delete mutation, toasts the failure via useShowMutationError, and toasts + calls onSuccess on
// deletion. The one failure it does not toast is the in-use refusal, which swaps the body instead.
export const DeleteIntegrationModal: React.FC<DeleteIntegrationModalProps> = ({ integration, onCancel, onSuccess }) => {
  const { addToast } = useToastService()
  // The server's refusal, which replaces the confirmation body. Held rather than toasted because
  // reassigning the repositories it names is what the user has to do next, and a toast neither
  // lasts long enough to work from nor has room for a list.
  const [inUseProblem, setInUseProblem] = useState<IntegrationInUseProblem | undefined>(undefined)
  const queryClient = useQueryClient()

  const showDeleteError = useShowMutationError<typeof useDeleteIntegration>({
    prefix: 'Failed to delete integration',
  })
  const deleteIntegration = useDeleteIntegration({
    onSuccess: () => {
      addToast({ message: <>{getInstanceName(integration)} removed.</>, variant: 'success' })
      onSuccess()
    },
    onError: (...args) => {
      const [error] = args
      if (reportRoleRefusal({ error, message: <>You do not have permission to remove integrations.</>, addToast })) {
        return
      }
      const problem = getIntegrationInUseProblem(error)
      if (problem) {
        // Outside the mounted check on purpose: the query client outlives this component, and a
        // refusal the user never saw is exactly when the cached count is most stale. Refreshes the
        // instance cards behind this modal; it does not reach the Manage drawer, which holds its
        // instance as a snapshot taken when it opened, so Delete stays offered there until reopened.
        queryClient.invalidateQueries({ queryKey: ['integrations'] })
        // Swapping the body is only a report while there is a body to swap. The mutation outlives
        // this component — the drawer behind it can close — so an answer arriving after the modal is
        // gone falls through to the toast, which is the only thing left that can carry it.
        if (isMountedRef.current) {
          setInUseProblem(problem)
          return
        }
      }
      // A standing refusal is deliberately left alone. Clearing it would swap a failed retry back to
      // the delete confirmation, re-arming a destructive button where the user last clicked Retry.
      showDeleteError(...args)
    },
  })

  const isMountedRef = useRef(true)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Swapping content inside an already-open dialog announces nothing, and the button the user just
  // activated is gone from the DOM, so focus would fall away with it. Focusing the refusal restores
  // the anchor and is what makes a screen reader read it — before this change the error toast's
  // `aria-live` was the announcement, and the blocked body deliberately suppresses it. The whole
  // refusal is the focus target rather than its heading alone, so the count and the repositories are
  // read out too.
  const blockedBodyRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const blockedBody = blockedBodyRef.current
    if (!inUseProblem || !blockedBody) {
      return
    }
    // A repeat refusal has to be announced again, and `focus()` on the already-focused element
    // fires nothing. Whether focus is still here depends on the browser — clicking a button moves
    // focus in Chrome but not in Safari — so the transition is forced rather than assumed.
    if (document.activeElement === blockedBody) {
      blockedBody.blur()
    }
    blockedBody.focus()
  }, [inUseProblem])

  const toolLabel = getProviderLabel(integration.type)
  const instanceName = getInstanceName(integration)
  // Sonar and AppScan, and the three source providers with a per-connection webhook route, are all
  // reached at a URL that names this instance, so deleting it breaks a delivery path the user set up
  // in a product Pixee does not administer. The setup-form test is what narrows the response's open
  // `type` to the catalog the copy is written against; a type with no form in the product has no
  // webhook copy either.
  const webhookWarning = getHasIntegrationSetupForm(integration.type)
    ? getWebhookDeleteWarning({ type: integration.type, toolLabel })
    : undefined

  // The refusal is left standing while a retry is in flight and replaced only when the next result
  // arrives. Clearing it here instead would flip the modal back to the delete confirmation — "This
  // action cannot be undone. Do you wish to proceed?" — for the whole duration of the request.
  const handleDelete = () => {
    invariant(integration.id, 'A configured integration always has an id')
    deleteIntegration.mutate({ integrationId: integration.id })
  }

  return (
    <Dialog.Root open onOpenChange={open => (!open ? onCancel() : undefined)}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        {/* A confirmational modal must not dismiss on an outside click — losing a destructive
            confirm to a stray click is the failure the pattern exists to prevent. */}
        <Dialog.Content
          className={styles.content}
          onPointerDownOutside={event => event.preventDefault()}
          // Dismissing a destructive confirmation mid-request would leave no report of whether the
          // instance was removed.
          onEscapeKeyDown={event => {
            if (deleteIntegration.isPending) {
              event.preventDefault()
            }
          }}
        >
          {/* The title asks the question in both states; only the body and the footer answer it
              differently, which is what design-spec.md §11 specifies changing. */}
          <Dialog.Title className={styles.title}>
            <WarningCircleIcon size={32} weight="fill" className={styles.icon} />
            Delete integration?
          </Dialog.Title>
          {/* The summary card leads the blocked body, so the instance the refusal is about is named
              before it, and the refusal itself is one focusable block the whole of which is read.
              In the confirm state the lead line comes first, as shipped. */}
          {!inUseProblem && (
            <Dialog.Description asChild>
              <p className={styles.description}>You are about to delete the following integration:</p>
            </Dialog.Description>
          )}
          {/* Kept in both states: it is what "this instance" refers to. */}
          <div className={styles.summaryCard}>
            <div className={styles.summaryHeader}>
              <IntegrationBrandIcon integration={integration} />
              <span>{toolLabel}</span>
            </div>
            {instanceName && <p className={styles.summaryName}>{instanceName}</p>}
          </div>
          {inUseProblem ? (
            <Dialog.Description asChild>
              <div ref={blockedBodyRef} tabIndex={-1} className={styles.blockedBody}>
                <p className={styles.blockedHeading}>This instance can&apos;t be deleted yet.</p>
                <InUseDetails problem={inUseProblem} toolLabel={toolLabel} />
              </div>
            </Dialog.Description>
          ) : (
            <>
              {webhookWarning && <p className={styles.description}>{webhookWarning}</p>}
              <p className={styles.description}>This action cannot be undone. Do you wish to proceed?</p>
            </>
          )}
          <div className={styles.buttons}>
            {inUseProblem ? (
              <>
                {/* Dismissive first, primary last, matching the confirm footer above — a user who
                    has just seen Cancel/Delete reaches for the right-hand button. Left enabled while
                    a retry runs: disabling it too would leave the dialog with nothing focusable, and
                    dismissing a refusal mid-retry costs the user nothing. */}
                <GhostButton size="small" onClick={onCancel} className={styles.cancelBtn}>
                  Close
                </GhostButton>
                {/* An empty list means the refusal stands but re-reading named nothing, so the
                    current state is unknown and retrying is the only way to learn it. With a list in
                    hand there is nothing to retry until the user has reassigned those repositories. */}
                {inUseProblem.repositories.length === 0 && (
                  <DefaultButton
                    buttonType="primary"
                    size="small"
                    state={deleteIntegration.isPending ? 'loading' : 'default'}
                    onClick={handleDelete}
                    loadingLabel="Deleting…"
                  >
                    Retry
                  </DefaultButton>
                )}
              </>
            ) : (
              <>
                <GhostButton
                  size="small"
                  onClick={onCancel}
                  state={deleteIntegration.isPending ? 'disabled' : 'default'}
                  className={styles.cancelBtn}
                >
                  Cancel
                </GhostButton>
                <DefaultButton
                  buttonType="destructive"
                  size="small"
                  state={deleteIntegration.isPending ? 'loading' : 'default'}
                  onClick={handleDelete}
                  loadingLabel="Deleting…"
                  icon={<TrashSimpleIcon size={14} />}
                >
                  Delete integration
                </DefaultButton>
              </>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

type InUseDetailsProps = {
  problem: IntegrationInUseProblem
  toolLabel: string
}

/**
 * What still references the instance. The platform names at most ten repositories however many
 * there are, so `repository_count` sizes the job and the list is a sample of it.
 */
const InUseDetails: React.FC<InUseDetailsProps> = ({ problem, toolLabel }) => {
  const { detail, repositories, repository_count: repositoryCount } = problem

  // An empty list is the platform's "the refusal stands but re-reading found nothing" branch, and it
  // carries a zero count. Naming a count and a tool to reassign to would both be wrong there, so
  // `detail` — which says what actually happened — stands in for the list.
  if (repositories.length === 0) {
    return <p className={styles.description}>{detail}</p>
  }

  const remaining = repositoryCount - repositories.length

  return (
    <div className={styles.blockedDetails}>
      <p className={styles.description}>
        {repositoryCount} {pluralize(repositoryCount, 'repository', 'repositories')}{' '}
        {pluralize(repositoryCount, 'uses', 'use')} it.
      </p>
      <p className={styles.description}>Reassign to another {toolLabel} instance first:</p>
      <ul className={styles.repositoryList}>
        {repositories.map(repository => (
          <li key={repository} className={styles.repositoryItem}>
            {repository}
          </li>
        ))}
      </ul>
      {remaining > 0 && <p className={styles.description}>…and {remaining} more.</p>}
    </div>
  )
}
